import React from 'react';
import { navigate } from '@reach/router';
import RouteBuilder from 'src/utils/routing/RouteBuilder';
import { useRouteBuilder } from 'src/utils/routing/RouteBuilderContext';
import { FunctionComponentReturnType } from 'src/types/sharedReact';
import 'src/pages/NotAuthorizedPage/NotAuthorizedPage.css';

import '@cof/dxo-ui-button';

// ====== CONSTANTS ======
const DEFAULT_RESTRICTED_HEADING = 'Access to this place or content is restricted.';
const DEFAULT_RESTRICTED_DESCRIPTION =
	'If you think this is a mistake, please contact your administrator or the person who directed you here.';
const RETURN_BUTTON_TEXT = '← Return to Home';

interface NotAuthorizedPageProps {
	/** Optional override for the page's primary heading. */
	heading?: string;
	/** Optional override for the description paragraph below the heading. */
	description?: string;
}

/**
 * NotAuthorizedPage - Restricted Access Notice
 *
 * Displayed when a user attempts to view a page they are not authorized to see,
 * when a legacy route has been blocked on the V2 (devex) host, or by any other
 * page that needs the same restricted-access UX.
 *
 * Callers can override the heading / description for context-specific copy
 * (e.g. a dashboard the user can't access vs. a generic page redirect).
 */
function NotAuthorizedPage({
	heading = DEFAULT_RESTRICTED_HEADING,
	description = DEFAULT_RESTRICTED_DESCRIPTION,
}: NotAuthorizedPageProps = {}): FunctionComponentReturnType {
	// ====== CONTEXT & HOOKS ======
	const routeBuilder: RouteBuilder = useRouteBuilder();

	// ====== EVENT HANDLERS ======
	/**
	 * Navigates back to the appropriate home page for the current host —
	 * V2 home on devex, legacy home on COPPv1.
	 */
	const handleReturn = (): void => {
		const isOnDevex = typeof window !== 'undefined'
			&& window.location.hostname.includes('developer');
		navigate(isOnDevex ? routeBuilder.client.toHomePageV2() : routeBuilder.client.toHomePage());
	};

	// ====== RENDER ======
	return (
		<div className="not-authorized-page">
			<div className="not-authorized-section">
				<div className="not-authorized-content">
					<h2>{heading}</h2>
					<p>{description}</p>
					<div className="not-authorized-return-button" onClick={handleReturn}>
						<dxo-ui-button
							style-variant="grv-button tertiary"
							id="copp-button-not-authorized-return"
							name="return-to-home"
							text={RETURN_BUTTON_TEXT}
							type="button"
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

export default NotAuthorizedPage;
